import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { useBusiness } from '../onboarding/useBusiness';
import {
  ALL_HISTORY,
  HISTORY_FILTERS,
  HISTORY_STATUS_PARAM,
  historyFilterFor,
  type HistoryFilter,
} from './historyFilters';
import { PostTile } from './PostTile';
import { useRequestHistory } from './useRequestHistory';

/** What an empty filtered list says, per filter. All has its own empty state. */
const EMPTY_FILTERED: Readonly<Record<Exclude<HistoryFilter['key'], 'all'>, string>> = {
  waiting: 'Nothing waiting on you.',
  done: 'No finished posts yet.',
  failed: 'No failed posts.',
  rejected: 'Nothing sent back.',
};

/**
 * Every request this business has made, newest first, as a grid of tiles.
 *
 * Deliberately a log and not a dashboard: rejected and failed runs stay on it,
 * because the trail of what was tried is the point. The status filter narrows
 * it server-side and lives in the URL (`?status=waiting`), so Home's "See all"
 * and a reload land on the same list. Paging is offset-based and accumulating —
 * "Load more" adds to what is on screen rather than replacing it.
 */
export function HistoryPage() {
  const { businessId, isLoading: isBusinessLoading, error: businessError } = useBusiness();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = historyFilterFor(searchParams.get(HISTORY_STATUS_PARAM));
  const history = useRequestHistory(businessId, { status: filter.statuses });

  function chooseFilter(next: HistoryFilter) {
    const params = new URLSearchParams(searchParams);
    if (next.key === 'all') params.delete(HISTORY_STATUS_PARAM);
    else params.set(HISTORY_STATUS_PARAM, next.key);
    // Replace, so Back leaves History instead of walking back through chips.
    setSearchParams(params, { replace: true });
  }

  return (
    <main className="page page--wide">
      <PageHeader
        section="History"
        title="History"
        lede="Every post, the version you chose and the slides that went out."
      />

      {isBusinessLoading ? (
        <p className="kb-section__status" role="status">
          Loading your history
        </p>
      ) : !businessId ? (
        <div className="kb-section">
          <p className="form__notice">
            {businessError ?? 'No business is linked to this account yet. Finish setup first.'}
          </p>
          <Link className="button" to="/onboarding">
            Finish setup
          </Link>
        </div>
      ) : (
        <section className="history" aria-label="Your posts">
          <div className="history-filter" role="group" aria-label="Filter by status">
            {HISTORY_FILTERS.map((option) => (
              <button
                key={option.key}
                className="history-filter__chip"
                type="button"
                aria-pressed={option.key === filter.key}
                onClick={() => chooseFilter(option)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {history.isLoading ? (
            <p className="kb-section__status" role="status">
              Loading your history
            </p>
          ) : history.requests.length === 0 && !history.error ? (
            filter.key === 'all' ? (
              <div className="kb-section__empty">
                <p className="kb-section__empty-text">No posts yet.</p>
                <Link className="button button--primary" to="/generation">
                  Create your first post
                </Link>
              </div>
            ) : (
              <p className="history__empty">
                {EMPTY_FILTERED[filter.key]}{' '}
                <button
                  className="link-button"
                  type="button"
                  onClick={() => chooseFilter(ALL_HISTORY)}
                >
                  Show all
                </button>
              </p>
            )
          ) : (
            <ul className="post-grid">
              {history.requests.map((request) => (
                <PostTile key={request.id} businessId={businessId} request={request} />
              ))}
            </ul>
          )}

          {history.error ? (
            <div className="history__foot">
              <p className="form__error" role="alert">
                {history.error}
              </p>
              <button className="button" type="button" onClick={history.retry}>
                Try again
              </button>
            </div>
          ) : history.hasMore ? (
            <div className="history__foot">
              <button
                className="button"
                type="button"
                onClick={history.loadMore}
                disabled={history.isLoadingMore}
              >
                {history.isLoadingMore ? 'Loading' : 'Load more'}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </main>
  );
}
